import * as React from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "../atoms/Tooltip"
import { cn } from "../utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { component?: React.ElementType }
>(({ className, component: Component = "div", ...props }, ref) => (
  <Component
    ref={ref}
    className={cn(
      "rounded-lg border border-border/80 bg-card text-card-foreground shadow-xs relative",
      className,
    )}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-2 p-6", className)} {...props} />
  ),
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("font-medium leading-none tracking-tight", className)}
      {...props}
    />
  ),
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { tooltip?: React.ReactNode }
>(({ className, tooltip, ...props }, ref) => {
  const description = (
    <div ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  )

  if (!tooltip) {
    return description
  }

  return (
    <Tooltip>
      <TooltipTrigger slick>{description}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
})
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  ),
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
)
CardFooter.displayName = "CardFooter"

const CardAction = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("absolute top-2 right-2 z-10 text-muted-foreground", className)}
      {...props}
    />
  ),
)
CardAction.displayName = "CardAction"

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle }
